import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ChannelCard from "@/components/base/ChannelCard";
import { useSeo } from "@/hooks/useSeo";
import { channels } from "@/mocks/gossip";
import { kindLabel } from "@/utils/format";

const filters = [
  { key: "all", label: "全部入口" },
  { key: "channel", label: "频道" },
  { key: "section", label: "版块" },
  { key: "column", label: "栏目" },
];

export default function ChannelsPage() {
  const [filter, setFilter] = useState("all");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    return channels.filter((c) => {
      const matchKind = filter === "all" || c.kind === filter;
      const q = keyword.trim();
      const matchKeyword = !q || c.name.includes(q) || c.intro.includes(q);
      return matchKind && matchKeyword;
    });
  }, [filter, keyword]);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "频道入口大全 - 看料爆料版块与栏目导航",
        url: "https://sdyjh.cn/channels",
        inLanguage: "zh-CN",
        description: "看料爆料频道入口大全，按频道、版块、栏目三类整理全部爆料入口。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sdyjh.cn/" },
          { "@type": "ListItem", position: 2, name: "频道入口大全", item: "https://sdyjh.cn/channels" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "看料爆料频道入口列表",
        numberOfItems: channels.length,
        itemListElement: channels.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          url: `https://sdyjh.cn/channels#${c.slug}`,
        })),
      },
    ],
    [],
  );

  useSeo({
    title: "频道入口大全 - 看料爆料版块、栏目导航 | 看料社",
    description:
      "看料爆料频道入口大全，按频道、版块、栏目三类整理全部爆料入口，支持关键词搜索与类型筛选，快速定位想看的瓜料方向。",
    keywords: "看料爆料频道,看料爆料版块,看料爆料栏目,爆料入口大全",
    canonicalPath: "/channels",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 pt-28 md:pt-36 pb-10 md:pb-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "频道入口大全" }]} />
            <h1 className="mt-5 font-heading font-black text-3xl md:text-5xl text-foreground-950 leading-tight">
              频道入口大全
            </h1>
            <p className="mt-4 text-sm md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
              这里是看料爆料专区所有版块、频道与栏目的集中入口。按类型筛选或直接搜索关键词，找到方向后点进去即可连续浏览。
            </p>

            <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-center gap-2 p-1.5 rounded-full bg-background-50 border border-background-200 w-full lg:w-auto overflow-x-auto">
                {filters.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      filter === f.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 px-4 py-3 rounded-full bg-background-50 border border-background-200 w-full lg:max-w-sm">
                <span className="w-6 h-6 flex items-center justify-center text-foreground-500 shrink-0">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索频道名称或描述"
                  aria-label="搜索频道名称或描述"
                  className="flex-1 min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 outline-none"
                />
                {keyword && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清空搜索"
                    className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  >
                    <i className="ri-close-line" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-600">
                共收录 <strong className="text-foreground-950">{list.length}</strong> 个入口
                {filter !== "all" && <span className="ml-1">· {kindLabel(filter)}</span>}
              </p>
            </div>

            {list.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {list.map((channel) => (
                  <div key={channel.slug} id={channel.slug}>
                    <ChannelCard channel={channel} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <span className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-2xl bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line" />
                </span>
                <p className="mt-4 text-sm text-foreground-500">没有找到匹配的入口，换个关键词或类型试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setFilter("all");
                  }}
                  className="mt-5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}